import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  askAIChatCall,
  askTutorChatCall,
  getAIMessagesByThreadCall,
} from "../../redux/api/ai.api";
import {
  invokeMcpToolCall,
  quizCopilotChatCall,
  uploadAssistantSourceCall,
} from "../../redux/api/mcpQuiz.api";
import {
  appendConversationMessagesCall,
  createConversationCall,
  deleteConversationCall,
  getAssistantSourceTextCall,
  getConversationCall,
  listConversationsCall,
  renameConversationCall,
  rewriteSectionCall,
  saveConversationDraftCall,
  updateAssistantSourceTextCall,
} from "../../redux/api/assistantConversations.api";
import { getQuizResultsCall } from "../../redux/api/quizSystem.api";
import { GetUniversityStudentsCall } from "../../redux/api/student.api";
import { getDashboardRouteContext } from "./routeContext";
import { getPreferOwnImages, getPresentationImageSource } from "../../prof/presentationTitles/imageSourcePreference";
import {
  clearPersistedConversation,
  conversationReducer,
  createMessage,
  initialConversationState,
  loadActiveConversationId,
  loadPersistedConversation,
  mapServerMessages,
  mapThreadMessages,
  persistActiveConversationId,
  persistConversation,
  sanitizeTutorContent,
} from "./AIConversationState";
import { canSendUserMessage, countMessages, getLimitState } from "./conversationLimit";
import * as deckOps from "./drafts/presentationDraftOps";
import * as quizOps from "./drafts/quizDraftOps";
import * as docOps from "./drafts/documentDraftOps";
import { looksLikeDeckEdit } from "./drafts/deckEditIntent";
import {
  looksLikeDesignRequest,
  requestPresentationDesign,
} from "./drafts/presentationDesignBridge";
import { advancePresentationSetup } from "./presentationSetup";
import { applyTitleSelection, catalogueName } from "./drafts/quizTitleSelection";
import {
  CONVERSATION_LIMIT_MESSAGE,
  MESSAGE_STATUS,
  RESULTS_OPTION_CHIPS,
  USERS_OPTION_CHIPS,
  formatQuizResultsAnalysis,
  formatUsersDirectory,
  getLocalAssistantFallback,
} from "./constants";
import { useQuizDraftOptional } from "./quiz/QuizDraftContext";
import {
  emptyQuizDraft,
  isPresentationCreateRequest,
  isPresentationIntent,
  isQuizCopilotIntent,
} from "./quiz/quizDraftDefaults";
import { getPlatformGuideReply } from "./platformGuide";
import {
  DATA_LANES,
  LANES,
  classifyTurn,
  detectDeliverable,
  isTaskLane,
  previousLane,
} from "./assistantIntent";
import { validateAssistantSourceFile } from "./AIChatInput";

/** What to build from an attached file. “A quiz out of this presentation” is a quiz. */
function attachedFileIntent(text) {
  const deliverable = detectDeliverable(text);
  return deliverable === "quiz" || deliverable === "presentation" ? deliverable : "ask";
}

function fileAskCopy(text, fileName, imageCount) {
  const raw = String(text || "");
  const n = Number(imageCount) || 0;
  const fa = /[پچژگ]/.test(raw) || /فارسی|persian|farsi/i.test(raw);
  const ar = !fa && /[\u0600-\u06FF]/.test(raw);
  const figures = n
    ? fa
      ? ` ${n} شکل از آن ذخیره شد.`
      : ar
        ? ` حفظت ${n} شكلًا منه.`
        : ` I saved ${n} figure${n === 1 ? "" : "s"} from it.`
    : fa
      ? " شکل جدایی پیدا نکردم، پس از متن استفاده می‌کنم."
      : ar
        ? " لم أجد أشكالًا منفصلة، سأستخدم النص."
        : "";
  if (fa) {
    return {
      reply: `«${fileName}» را خواندم.${figures} از آن آزمون بسازم، ارائه بسازم، یا سؤالی درباره‌اش دارید؟`,
      quizLabel: "آزمون بساز",
      presentationLabel: "ارائه بساز",
      summaryLabel: "نکات کلیدی را خلاصه کن",
    };
  }
  if (ar) {
    return {
      reply: `قرأت «${fileName}».${figures} هل أعمل اختبارًا أو عرضًا منه، أم لديك سؤال عنه؟`,
      quizLabel: "أنشئ اختبارًا",
      presentationLabel: "أنشئ عرضًا",
      summaryLabel: "لخّص النقاط الأساسية",
    };
  }
  return {
    reply: `I read “${fileName}”.${figures} Should I make a quiz or a presentation from it, or do you have a question about it?`,
    quizLabel: "Make a quiz",
    presentationLabel: "Make a presentation",
    summaryLabel: "Summarize the key points",
  };
}

function deckImportPending(draft) {
  const row = draft?.source_import;
  if (!row?.source_id) return false;
  if (!row.scope && !row.fidelity) return true;
  if (row.scope === "parts" && !String(row.parts || "").trim()) return true;
  return row.library == null;
}

function looksLikeDeckAnswer(text) {
  const t = String(text || "").trim().toLowerCase();
  if (!t) return false;
  return (
    /\b(exactly|as is|as-is|new lesson|zoomable|keep only|high-resolution|high resolution)\b/.test(t) ||
    /\bpower\s?points?\b/.test(t) ||
    /\b(import|whole file|some parts?|only some|3d|models)\b/.test(t)
  );
}

const UNDO_DEPTH = 30;
const DRAFT_SAVE_DELAY_MS = 700;

function newRequestId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** True when the drafts hold something worth saving to a conversation. */
function hasDraftWork(quiz, doc) {
  return Boolean(
    quiz?.pending_presentation?.slides?.length ||
      quiz?.pending_generated_questions?.questions?.length ||
      quiz?.awaiting_confirmation ||
      (quiz?.topic_rules || []).length ||
      doc?.sections?.length
  );
}

const AIAssistantContext = createContext(null);
AIAssistantContext.displayName = "AIAssistantContext";

function resolveAuthUserId(auth) {
  if (!auth) return null;
  return auth.id || auth.user_id || auth.user?.id || auth.email || null;
}

function hasAccessToken(auth) {
  return Boolean(auth?.access_token);
}

function buildHistory(messages) {
  return (messages || [])
    .filter((m) => m.status !== MESSAGE_STATUS.FAILED)
    .slice(-12)
    .map((m) => ({
      role: m.role === "assistant" ? "assistant" : "user",
      content: String(m.content || "").slice(0, 2000),
    }));
}

export function AIAssistantProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const userId = resolveAuthUserId(auth);
  const isAuthed = hasAccessToken(auth);
  const quizDraftApi = useQuizDraftOptional();

  const [state, dispatch] = useReducer(conversationReducer, initialConversationState);
  const [sourceAttachment, setSourceAttachment] = useState(null);
  const sourceRef = useRef(null);
  // The last file used in this conversation; follow-ups (“make slides from it”,
  // “what does slide 5 say?”) send it again without re-attaching.
  const recentSourceRef = useRef(null);
  const [copilotUi, setCopilotUi] = useState({
    confirmation: null,
    pendingPresentation: null,
    createdQuiz: null,
    optionChips: [],
    confirming: false,
  });
  const restoreAttemptedRef = useRef(null);
  const confirmRequestIdRef = useRef(null);
  // Guided "Make a presentation": { step: "topic" } → { step: "count", topic } → request.
  const presentationSetupRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // In-chat document workspace + undo stack for reversible draft edits.
  const [documentDraft, setDocumentDraft] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [history, setHistory] = useState({
    open: false,
    loading: false,
    items: [],
    query: "",
    error: null,
  });
  const quizDraftRef = useRef(quizDraftApi?.draft || null);
  quizDraftRef.current = quizDraftApi?.draft || null;
  const documentDraftRef = useRef(null);
  documentDraftRef.current = documentDraft;
  const syncedIdsRef = useRef(new Set());
  const syncChainRef = useRef(Promise.resolve());
  const conversationPromiseRef = useRef(null);
  const generationRef = useRef(0);
  const draftSaveTimerRef = useRef(null);
  const routeKeyRef = useRef(null);

  const routeContext = useMemo(
    () => getDashboardRouteContext(location.pathname),
    [location.pathname]
  );
  routeKeyRef.current = routeContext?.key || null;
  const dashboardContext = useCallback(
    () => ({
      ...routeContext,
      imageSource: getPresentationImageSource(),
      preferOwnImages: getPreferOwnImages(),
    }),
    [routeContext]
  );

  useEffect(() => {
    if (!isAuthed || !userId) {
      if (state.ownerUserId) {
        dispatch({ type: "RESET_FOR_SECURITY" });
      }
      restoreAttemptedRef.current = null;
      return;
    }

    if (state.ownerUserId === String(userId)) return;

    const saved = loadPersistedConversation(userId);
    dispatch({ type: "BIND_OWNER", ownerUserId: String(userId) });
    syncedIdsRef.current = new Set();
    if (saved) {
      dispatch({
        type: "HYDRATE",
        payload: { ...saved, ownerUserId: String(userId) },
      });
      // Cached messages were already stored when they were sent.
      (saved.messages || []).forEach((m) => syncedIdsRef.current.add(m.id));
    }
    const activeId = loadActiveConversationId(userId) || saved?.conversationId || null;
    if (activeId) loadConversationRef.current?.(activeId, { keepInput: true, quiet: true });
  }, [isAuthed, userId, state.ownerUserId]);

  useEffect(() => {
    if (!state.ownerUserId) return;
    persistConversation(state.ownerUserId, {
      threadId: state.threadId,
      conversationId: state.conversationId,
      conversationTitle: state.conversationTitle,
      messages: state.messages,
      draft: state.draft,
    });
    persistActiveConversationId(state.ownerUserId, state.conversationId);
  }, [
    state.ownerUserId,
    state.threadId,
    state.conversationId,
    state.conversationTitle,
    state.messages,
    state.draft,
  ]);

  /* ---------- Backend conversation: create lazily, sync messages, save drafts ---------- */

  const buildDraftState = useCallback(
    () => ({
      version: 1,
      quiz: quizDraftRef.current || null,
      document: documentDraftRef.current || null,
    }),
    []
  );

  const ensureConversation = useCallback(async () => {
    if (stateRef.current.conversationId) return stateRef.current.conversationId;
    if (!stateRef.current.ownerUserId) return null;
    if (!conversationPromiseRef.current) {
      const generation = generationRef.current;
      conversationPromiseRef.current = (async () => {
        const res = await createConversationCall({
          section_key: routeKeyRef.current,
          draft_state: buildDraftState(),
        });
        const created = res?.conversation;
        if (!created?.id || generation !== generationRef.current) return null;
        stateRef.current = { ...stateRef.current, conversationId: created.id };
        dispatch({ type: "SET_CONVERSATION", conversationId: created.id, title: created.title });
        return created.id;
      })().finally(() => {
        conversationPromiseRef.current = null;
      });
    }
    return conversationPromiseRef.current;
  }, [buildDraftState]);

  // Store each sent user prompt / assistant reply once (ids make retries idempotent).
  // Failed sends are never stored, so they never count toward the limit.
  useEffect(() => {
    if (!state.ownerUserId || !isAuthed) return;
    const pending = state.messages.filter(
      (m) =>
        m.status === MESSAGE_STATUS.SENT &&
        (m.role === "user" || m.role === "assistant") &&
        !syncedIdsRef.current.has(m.id)
    );
    if (!pending.length) return;
    pending.forEach((m) => syncedIdsRef.current.add(m.id));
    const generation = generationRef.current;
    const release = () => pending.forEach((m) => syncedIdsRef.current.delete(m.id));
    syncChainRef.current = syncChainRef.current
      .then(async () => {
        if (generation !== generationRef.current) return;
        const id = await ensureConversation();
        if (!id || generation !== generationRef.current) {
          release();
          return;
        }
        const res = await appendConversationMessagesCall(
          id,
          pending.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.createdAt,
            event: Boolean(m.event),
          }))
        );
        if (generation !== generationRef.current) return;
        if (res?.code === "CONVERSATION_LIMIT") {
          dispatch({ type: "SET_SERVER_LIMIT", reached: true });
          return;
        }
        if (!res || (typeof res.status === "number" && res.status >= 400)) {
          release();
          return;
        }
        if (res.conversation?.title) {
          dispatch({ type: "SET_CONVERSATION", conversationId: id, title: res.conversation.title });
        }
      })
      .catch(release);
  }, [state.messages, state.ownerUserId, isAuthed, ensureConversation]);

  const saveDraftsNow = useCallback(async () => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    const quiz = quizDraftRef.current;
    const doc = documentDraftRef.current;
    let id = stateRef.current.conversationId;
    if (!id) {
      if (!hasDraftWork(quiz, doc)) return;
      id = await ensureConversation();
    }
    if (!id) return;
    await saveConversationDraftCall(id, buildDraftState());
  }, [buildDraftState, ensureConversation]);

  // Drafts are saved to the conversation (debounced) so they survive refreshes,
  // navigation, closing the panel and switching conversations.
  useEffect(() => {
    if (!state.ownerUserId || !isAuthed) return undefined;
    if (!state.conversationId && !hasDraftWork(quizDraftApi?.draft, documentDraft)) return undefined;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      draftSaveTimerRef.current = null;
      saveDraftsNow().catch(() => {});
    }, DRAFT_SAVE_DELAY_MS);
    return undefined;
  }, [quizDraftApi?.draft, documentDraft, state.ownerUserId, state.conversationId, isAuthed, saveDraftsNow]);

  useEffect(
    () => () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (quizDraftApi?.draft && quizDraftApi.draft.awaiting_confirmation === false) {
      setCopilotUi((prev) =>
        prev.confirmation ? { ...prev, confirmation: null } : prev
      );
    }
  }, [quizDraftApi?.draft?.awaiting_confirmation]);

  useEffect(() => {
    if (!isAuthed || !userId || !state.threadId) return;
    if (state.messages.length > 0) return;
    const attemptKey = `${userId}:${state.threadId}`;
    if (restoreAttemptedRef.current === attemptKey) return;
    restoreAttemptedRef.current = attemptKey;

    let cancelled = false;
    (async () => {
      dispatch({ type: "SET_RESTORING", restoring: true });
      try {
        const res = await getAIMessagesByThreadCall(state.threadId);
        if (cancelled) return;
        const mapped = mapThreadMessages(res);
        if (mapped.length) {
          dispatch({ type: "SET_MESSAGES", messages: mapped });
        }
      } catch {
        if (!cancelled) {
          dispatch({
            type: "SET_ERROR",
            error: "Could not restore the previous conversation. You can continue or start a new one.",
          });
        }
      } finally {
        if (!cancelled) dispatch({ type: "SET_RESTORING", restoring: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthed, userId, state.threadId, state.messages.length]);

  const open = useCallback(() => dispatch({ type: "OPEN" }), []);
  const close = useCallback(() => dispatch({ type: "CLOSE" }), []);
  const toggle = useCallback(() => dispatch({ type: "TOGGLE" }), []);
  const setDraft = useCallback((draft) => dispatch({ type: "SET_DRAFT", draft }), []);
  const clearError = useCallback(() => dispatch({ type: "SET_ERROR", error: null }), []);

  const clearSourceAttachment = useCallback(() => {
    sourceRef.current = null;
    setSourceAttachment(null);
  }, []);

  const attachSourceFile = useCallback(async (file) => {
    const problem = validateAssistantSourceFile(file);
    if (problem) {
      sourceRef.current = null;
      setSourceAttachment({
        name: file?.name || "File",
        size: file?.size || 0,
        status: "error",
        error: problem,
        imageCount: 0,
        id: null,
      });
      return;
    }
    const pending = {
      name: file.name,
      size: file.size,
      status: "uploading",
      error: "",
      imageCount: 0,
      id: null,
    };
    sourceRef.current = pending;
    setSourceAttachment(pending);
    const res = await uploadAssistantSourceCall(file);
    const source = res?.source;
    if (!source?.id || (typeof res?.status === "number" && res.status >= 400)) {
      const failed = {
        ...pending,
        status: "error",
        error: res?.message || "Could not read that file.",
      };
      sourceRef.current = null;
      setSourceAttachment(failed);
      return;
    }
    const ready = {
      ...pending,
      status: "ready",
      id: source.id,
      imageCount: Number(source.image_count) || 0,
    };
    sourceRef.current = ready;
    setSourceAttachment(ready);
    // Open the extracted text as an editable document draft in the chat.
    const textRes = await getAssistantSourceTextCall(source.id);
    if (textRes?.source?.text) {
      const doc = docOps.createDocumentDraft(
        { id: source.id, file_name: source.file_name || file.name },
        textRes.source.text
      );
      setDocumentDraft({ ...doc, saved_sections: doc.sections, image_count: ready.imageCount });
    }
  }, []);

  const resetCopilotUi = useCallback((quizDraft = null) => {
    setCopilotUi({
      confirmation: quizDraft?.awaiting_confirmation ? previewFromDraft(quizDraft) : null,
      pendingPresentation: quizDraft?.pending_presentation || null,
      createdQuiz: null,
      optionChips: [],
      confirming: false,
      quizEditorOpen: false,
      titlePicker: null,
    });
  }, []);

  /**
   * Fresh conversation; the current one stays in history with its drafts saved.
   * `carryDrafts` moves unapproved drafts into the new conversation (used at the
   * limit) and clears them from the old one, so they can't be approved twice.
   * The unsent input is always kept.
   */
  const startNewConversation = useCallback(
    async ({ carryDrafts = false } = {}) => {
      const owner = stateRef.current.ownerUserId;
      const previousId = stateRef.current.conversationId;
      try {
        await saveDraftsNow();
        if (carryDrafts && previousId) await saveConversationDraftCall(previousId, null);
      } catch {
        // Drafts stay in local state either way.
      }
      generationRef.current += 1;
      syncedIdsRef.current = new Set();
      dispatch({ type: "NEW_CONVERSATION", carryDraft: true });
      if (owner) {
        persistConversation(owner, {
          threadId: null,
          conversationId: null,
          messages: [],
          draft: stateRef.current.draft,
        });
        persistActiveConversationId(owner, null);
      }
      restoreAttemptedRef.current = null;
      confirmRequestIdRef.current = null;
      presentationSetupRef.current = null;
      sourceRef.current = null;
      recentSourceRef.current = null;
      setSourceAttachment(null);
      setUndoStack([]);
      setHistory((prev) => ({ ...prev, open: false }));
      if (carryDrafts) {
        setCopilotUi((prev) => ({ ...prev, createdQuiz: null, optionChips: [], confirming: false }));
      } else {
        resetCopilotUi(null);
        quizDraftApi?.clearDraft?.();
        setDocumentDraft(null);
      }
    },
    [quizDraftApi, resetCopilotUi, saveDraftsNow]
  );

  const loadConversationRef = useRef(null);
  const loadConversation = useCallback(
    async (id, { keepInput = true, quiet = false } = {}) => {
      if (!id) return false;
      if (!quiet) {
        try {
          await saveDraftsNow();
        } catch {
          // ignore
        }
      }
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      if (!quiet) dispatch({ type: "SET_RESTORING", restoring: true });
      const res = await getConversationCall(id);
      if (generation !== generationRef.current) return false;
      if (!quiet) dispatch({ type: "SET_RESTORING", restoring: false });
      const conv = res?.conversation;
      if (!conv?.id) {
        if (quiet) {
          // Deleted elsewhere or not ours: forget it and keep a fresh panel.
          persistActiveConversationId(stateRef.current.ownerUserId, null);
          dispatch({ type: "SET_CONVERSATION", conversationId: null, title: null });
        } else {
          dispatch({
            type: "SET_ERROR",
            error: res?.message || "Couldn't open that conversation.",
          });
        }
        return false;
      }
      const messages = mapServerMessages(conv.messages);
      syncedIdsRef.current = new Set(messages.map((m) => m.id));
      dispatch({
        type: "LOAD_CONVERSATION",
        conversationId: conv.id,
        title: conv.title,
        messages,
        limitReached: conv.limit_reached,
        carryDraft: keepInput,
      });
      const quiz = conv.draft_state?.quiz || null;
      if (quiz) quizDraftApi?.setDraft?.(quiz);
      else quizDraftApi?.clearDraft?.();
      setDocumentDraft(conv.draft_state?.document || null);
      resetCopilotUi(quiz);
      setUndoStack([]);
      confirmRequestIdRef.current = null;
      presentationSetupRef.current = null;
      sourceRef.current = null;
      recentSourceRef.current = quiz?.source_document_id ? { id: quiz.source_document_id } : null;
      setSourceAttachment(null);
      setHistory((prev) => ({ ...prev, open: false }));
      return true;
    },
    [quizDraftApi, resetCopilotUi, saveDraftsNow]
  );
  loadConversationRef.current = loadConversation;

  /* ---------- History panel ---------- */

  const refreshHistory = useCallback(async (query) => {
    const q = query !== undefined ? query : "";
    setHistory((prev) => ({ ...prev, loading: true, error: null, query: q }));
    const res = await listConversationsCall({ q });
    setHistory((prev) => {
      if (prev.query !== q) return prev;
      if (!Array.isArray(res?.conversations)) {
        return { ...prev, loading: false, error: res?.message || "Couldn't load your history." };
      }
      return { ...prev, loading: false, items: res.conversations };
    });
  }, []);

  const openHistory = useCallback(() => {
    setHistory((prev) => ({ ...prev, open: true }));
    refreshHistory("");
  }, [refreshHistory]);

  const closeHistory = useCallback(() => {
    setHistory((prev) => ({ ...prev, open: false }));
  }, []);

  const renameConversation = useCallback(async (id, title) => {
    const res = await renameConversationCall(id, title);
    const updated = res?.conversation;
    if (!updated?.id) return res?.message || "Couldn't rename that conversation.";
    setHistory((prev) => ({
      ...prev,
      items: prev.items.map((c) => (c.id === id ? { ...c, ...updated } : c)),
    }));
    if (stateRef.current.conversationId === id) {
      dispatch({ type: "SET_CONVERSATION", conversationId: id, title: updated.title });
    }
    return null;
  }, []);

  /** Deletes only the conversation record — never presentations, quizzes or documents. */
  const deleteConversation = useCallback(
    async (id) => {
      const res = await deleteConversationCall(id);
      if (!res?.deleted) return res?.message || "Couldn't delete that conversation.";
      setHistory((prev) => ({ ...prev, items: prev.items.filter((c) => c.id !== id) }));
      if (stateRef.current.conversationId === id) {
        generationRef.current += 1;
        syncedIdsRef.current = new Set();
        if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
        dispatch({ type: "NEW_CONVERSATION", carryDraft: true });
        quizDraftApi?.clearDraft?.();
        setDocumentDraft(null);
        resetCopilotUi(null);
        setUndoStack([]);
      }
      return null;
    },
    [quizDraftApi, resetCopilotUi]
  );

  // Keep draft card in sync when backend updates pending_presentation on the quiz draft.
  useEffect(() => {
    const pending = quizDraftApi?.draft?.pending_presentation || null;
    setCopilotUi((prev) =>
      prev.pendingPresentation === pending
        ? prev
        : { ...prev, pendingPresentation: pending }
    );
  }, [quizDraftApi?.draft?.pending_presentation]);

  const applyCopilotResponse = useCallback(
    (res) => {
      if (res?.draft && quizDraftApi) {
        quizDraftApi.applyCopilotResult(res);
      }
      const options = Array.isArray(res?.ui_actions?.options) ? res.ui_actions.options : [];
      const nextPending =
        res?.pending_presentation !== undefined
          ? res.pending_presentation
          : res?.draft?.pending_presentation !== undefined
            ? res.draft.pending_presentation
            : undefined;
      const nextConfirmation =
        res?.ui_actions?.confirmation ||
        (res?.needs_confirmation ? res?.ui_actions?.confirmation : null) ||
        (res?.needs_confirmation ? previewFromDraft(res.draft) : null) ||
        null;
      // Don't let quiz + presentation UI cards fight each other.
      setCopilotUi((prev) => {
        const pending =
          nextPending !== undefined ? nextPending : prev.pendingPresentation;
        const confirmation = nextConfirmation;
        if (pending?.slides?.length) {
          return {
            ...prev,
            confirmation: null,
            pendingPresentation: pending,
            createdQuiz: res?.quiz || res?.ui_actions?.created_quiz || prev.createdQuiz,
            optionChips: options,
            confirming: false,
          };
        }
        if (confirmation) {
          return {
            ...prev,
            confirmation,
            pendingPresentation: null,
            createdQuiz: res?.quiz || res?.ui_actions?.created_quiz || prev.createdQuiz,
            optionChips: options,
            confirming: false,
          };
        }
        return {
          ...prev,
          confirmation: null,
          pendingPresentation: nextPending !== undefined ? nextPending : prev.pendingPresentation,
          createdQuiz: res?.quiz || res?.ui_actions?.created_quiz || prev.createdQuiz,
          optionChips: options,
          confirming: false,
        };
      });
      const presentationId =
        res?.ui_actions?.presentation_id ||
        res?.presentation?.id ||
        null;
      const navTo =
        res?.ui_actions?.navigate_to ||
        (presentationId ? `/presentationMakerPage/${presentationId}` : null);
      // A newly created presentation is offered as a chip ("Open presentation maker");
      // the assistant stays where the user is.
      const createdPresentation = Boolean(res?.presentation?.id);
      if (navTo && typeof navTo === "string" && !createdPresentation) {
        navigate(navTo, { replace: false });
      }
      if (res?.ui_actions?.reload_presentation || presentationId) {
        window.dispatchEvent(
          new CustomEvent("microlab:presentation-reload", {
            detail: { id: presentationId },
          })
        );
      }
    },
    [navigate, quizDraftApi]
  );

  const shouldUseQuizCopilot = useCallback(
    (text) => {
      const raw = String(text || "").trim();
      const awaitingQuiz = !!quizDraftApi?.draft?.awaiting_confirmation || !!copilotUi.confirmation;
      const awaitingPresentation =
        !!quizDraftApi?.draft?.pending_presentation ||
        !!copilotUi.pendingPresentation?.slides?.length;

      // Affirmations only continue the active draft — never jump lanes.
      if (/^(yes|confirm|ok|okay|sure|looks good)\b/i.test(raw)) {
        return awaitingQuiz || awaitingPresentation || deckImportPending(quizDraftApi?.draft);
      }

      if (isPresentationIntent(raw)) return true;
      if (awaitingPresentation && /^(presentation_|propose_presentation)/i.test(raw)) return true;
      if (awaitingPresentation && looksLikeDeckEdit(raw)) return true;

      if (quizDraftApi?.draft?.pending_generated_questions) return true;
      if (awaitingQuiz) return true;
      if ((quizDraftApi?.draft?.topic_rules || []).length > 0 && isQuizCopilotIntent(raw)) {
        return true;
      }
      if (
        /^(generate_missing|confirm_save_generated|discard_generated|open_question_bank|open_copilot|continue_quiz)/i.test(
          raw
        )
      ) {
        return true;
      }
      return isQuizCopilotIntent(raw);
    },
    [quizDraftApi, copilotUi.confirmation, copilotUi.pendingPresentation]
  );

  const applyAssistantText = useCallback((userMsgId, reply, options = []) => {
    dispatch({
      type: "UPDATE_MESSAGE",
      id: userMsgId,
      patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
    });
    dispatch({
      type: "APPEND_MESSAGE",
      message: createMessage({
        role: "assistant",
        content: reply,
        status: MESSAGE_STATUS.SENT,
      }),
    });
    setCopilotUi((prev) => ({
      ...prev,
      optionChips: (options || []).map((o) => ({
        id: o.id,
        label: o.label,
        prompt: o.prompt || o.label,
      })),
      confirmation: null,
      confirming: false,
    }));
  }, []);

  const loadResultsAnalysisLocally = useCallback(async () => {
    try {
      const res = await getQuizResultsCall({ page: 1, limit: 25 });
      if (res?.status === 401 || res?.status === 403) {
        return {
          reply:
            res?.message ||
            "You are not authorized to view quiz results for these classes.",
          options: RESULTS_OPTION_CHIPS,
        };
      }
      if (!res || (typeof res.status === "number" && res.status >= 400)) {
        return {
          reply:
            "I couldn’t load quiz results right now. Open the Results page or try again in a moment.",
          options: RESULTS_OPTION_CHIPS,
        };
      }
      return formatQuizResultsAnalysis(res);
    } catch {
      return {
        reply:
          "I couldn’t load quiz results right now. Open the Results page or try again in a moment.",
        options: RESULTS_OPTION_CHIPS,
      };
    }
  }, []);

  const loadUsersDirectoryLocally = useCallback(async () => {
    try {
      const role = String(auth?.role || auth?.user?.role || "")
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      const universityId =
        auth?.university_id || auth?.user?.university_id || auth?.universityId || null;
      const isUniAdmin = role === "university_admin";

      if (isUniAdmin && universityId) {
        const students = await GetUniversityStudentsCall(universityId);
        return formatUsersDirectory(students, { universityWide: true });
      }

      // Professors: offline path stays class-scoped via unique students in quiz results.
      const res = await getQuizResultsCall({ page: 1, limit: 50 });
      const body = res?.data && typeof res.data === "object" ? res.data : res;
      const results = Array.isArray(body?.results) ? body.results : [];
      const byEmail = new Map();
      results.forEach((r) => {
        const key = r.student_email || r.user_id || r.student_name;
        if (!key || byEmail.has(key)) return;
        byEmail.set(key, {
          name: r.student_name || "Student",
          email: r.student_email || "—",
          role: "student",
        });
      });
      return formatUsersDirectory([...byEmail.values()], { universityWide: false });
    } catch {
      return {
        reply:
          "I couldn’t load users right now. Try again in a moment, or ask about marks once Quiz Copilot is back online.",
        options: USERS_OPTION_CHIPS,
      };
    }
  }, [auth]);

  const sendMessage = useCallback(
    async (rawText, { retryOfId, confirmCreate = false, sourceId = null } = {}) => {
      const text = String(rawText || "").trim();
      const activeSource = sourceRef.current?.status === "ready" ? sourceRef.current : null;
      const resolvedSourceId = sourceId || activeSource?.id || null;
      if ((!text && !confirmCreate && !resolvedSourceId) || stateRef.current.sending) return;
      if (sourceRef.current?.status === "uploading") {
        dispatch({
          type: "SET_ERROR",
          error: "Still reading the file. You can send it in a moment.",
        });
        return;
      }

      // Chip shortcut — don't add a chat turn.
      if (text === "__open_results__") {
        navigate("/quiz/results");
        return;
      }
      const navMatch = text.match(/^__navigate__:(.+)$/);
      if (navMatch?.[1]) {
        navigate(navMatch[1].trim());
        return;
      }

      if (!isAuthed || !hasAccessToken(auth)) {
        dispatch({
          type: "SET_ERROR",
          error: "Please sign in again to use the AI assistant.",
        });
        return;
      }

      // Failed messages are not counted, so a retry needs no special case.
      if (
        stateRef.current.serverLimitReached ||
        !canSendUserMessage(countMessages(stateRef.current.messages))
      ) {
        dispatch({ type: "SET_ERROR", error: CONVERSATION_LIMIT_MESSAGE });
        return;
      }

      dispatch({ type: "SET_ERROR", error: null });

      if (retryOfId) {
        dispatch({ type: "REMOVE_MESSAGE", id: retryOfId });
      }

      const typedText = confirmCreate
        ? text || "Create Quiz"
        : text || `Attached ${activeSource?.name || "a file"}`;
      // One decision for this turn, using the earlier turns for follow-ups
      // (“create a new one”, “show it to me”, “no, use the Microlab bank”).
      const pendingDeck = Boolean(
        quizDraftApi?.draft?.pending_presentation?.slides?.length ||
          copilotUi.pendingPresentation?.slides?.length
      );
      const turnContext = {
        lastLane: previousLane(quizDraftApi?.draft, buildHistory(stateRef.current.messages)),
        hasQuizDraft: (quizDraftApi?.draft?.topic_rules || []).length > 0,
        awaitingQuiz: !!quizDraftApi?.draft?.awaiting_confirmation || !!copilotUi.confirmation,
        hasPendingPresentation: pendingDeck,
        awaitingTitle: !!quizDraftApi?.draft?.awaiting_title,
        hasSource: Boolean(resolvedSourceId),
        fileInContext: Boolean(resolvedSourceId || recentSourceRef.current?.id),
      };
      // A different task cancels a half-finished “what topic / how many slides” setup.
      if (presentationSetupRef.current && !confirmCreate) {
        const early = classifyTurn(typedText, { ...turnContext, lastLane: LANES.PRESENTATION });
        if (early.source === "explicit" && early.lane !== LANES.PRESENTATION && early.lane !== LANES.CHAT) {
          presentationSetupRef.current = null;
        }
      }
      // Bare "Make a presentation" asks for the topic, then the slide count,
      // before anything is generated. The bubble shows what the user typed;
      // routing uses the completed request.
      const setupStep =
        confirmCreate || resolvedSourceId
          ? null
          : advancePresentationSetup(presentationSetupRef.current, typedText);
      if (setupStep) presentationSetupRef.current = setupStep.setup;
      const displayText = setupStep?.request || typedText;
      const turn = classifyTurn(displayText, turnContext);
      const fileIntent = resolvedSourceId ? attachedFileIntent(text) : null;
      const answeringDeck =
        deckImportPending(quizDraftApi?.draft) ||
        (Boolean(resolvedSourceId) && looksLikeDeckAnswer(displayText));
      const resultsIntent = turn.lane === LANES.RESULTS;
      const usersIntent = turn.lane === LANES.USERS;
      const presentationIntent =
        turn.lane === LANES.PRESENTATION && !(pendingDeck && looksLikeDeckEdit(displayText));
      const quizIntent = turn.lane === LANES.QUIZ || turn.lane === LANES.QUIZ_LOOKUP;
      // Quiz, presentation, students, results, lookups and “which one?” questions are
      // answered from Microlab data — never by the general tutor chat.
      const copilotLane = DATA_LANES.has(turn.lane) || turn.lane === LANES.CLARIFY;
      // Suggestion chips follow this turn's task (set before any reply arrives).
      setCopilotUi((prev) => ({ ...prev, activeLane: turn.lane }));
      // Locally answered turns record their task too (the server does it for its turns).
      if (isTaskLane(turn.lane)) {
        quizDraftApi?.setDraft?.((d) => ({ ...d, last_lane: turn.lane }));
      }
      const userMsg = createMessage({
        role: "user",
        content: typedText,
        status: MESSAGE_STATUS.PENDING,
        retryPayload: { text: displayText, sourceId: resolvedSourceId },
      });
      const inputBeforeSend = stateRef.current.draft;
      dispatch({ type: "APPEND_MESSAGE", message: userMsg });
      dispatch({ type: "SET_DRAFT", draft: "" });
      dispatch({ type: "SET_SENDING", sending: true });

      if (setupStep?.reply) {
        applyAssistantText(userMsg.id, setupStep.reply, setupStep.options);
        dispatch({ type: "SET_SENDING", sending: false });
        return;
      }

      // Styling requests in the Presentation Maker ("apply a cinematic theme",
      // "add smooth transitions") are applied by the open editor, which owns
      // autosave, undo and the professor's edit permissions.
      if (
        routeContext?.key === "presentation_maker" &&
        !resolvedSourceId &&
        !confirmCreate &&
        looksLikeDesignRequest(displayText)
      ) {
        const designReply = await requestPresentationDesign(displayText);
        if (designReply?.text) {
          applyAssistantText(userMsg.id, designReply.text, designReply.options || []);
          dispatch({ type: "SET_SENDING", sending: false });
          return;
        }
      }

      // Attached with no instruction: offer quiz / presentation / summary. A typed
      // question about the file goes to the copilot, which answers from the file.
      if (resolvedSourceId && fileIntent === "ask" && !text && !confirmCreate && !answeringDeck) {
        const fileName = activeSource?.name || "that file";
        const asked = fileAskCopy(displayText, fileName, activeSource?.imageCount);
        applyAssistantText(
          userMsg.id,
          asked.reply,
          [
            {
              id: "source_quiz",
              label: asked.quizLabel,
              prompt: "Make a quiz from the uploaded file",
            },
            {
              id: "source_presentation",
              label: asked.presentationLabel,
              prompt: "Make a presentation from the uploaded file",
            },
            {
              id: "source_summary",
              label: asked.summaryLabel,
              prompt: "Summarize the key points of the uploaded file",
            },
          ]
        );
        dispatch({ type: "SET_SENDING", sending: false });
        return;
      }

      // Switching lanes: clear the other card so quiz/presentation never mix in the UI.
      if (presentationIntent) {
        setCopilotUi((prev) => ({
          ...prev,
          confirmation: null,
          createdQuiz: null,
          optionChips: [],
        }));
        quizDraftApi?.setDraft?.((d) => ({
          ...d,
          awaiting_confirmation: false,
          confirmation_fingerprint: null,
        }));
      } else if (quizIntent || confirmCreate || resultsIntent || usersIntent) {
        setCopilotUi((prev) => ({
          ...prev,
          pendingPresentation: null,
        }));
        quizDraftApi?.setDraft?.((d) => ({ ...d, pending_presentation: null }));
      }

      try {
        // Local starter / platform guide / offline create replies.
        // Results/users/marks always go through live data (never create-quiz stubs).
        const seededDraft = quizDraftApi?.draft || null;
        const guideIntent = turn.lane === LANES.GUIDE;
        const guideLocal =
          guideIntent &&
          getPlatformGuideReply(displayText, {
            pathname: routeContext.pathname || location.pathname,
            sectionKey: routeContext.key,
          });
        const local =
          guideLocal || getLocalAssistantFallback(displayText, { draft: seededDraft });
        const bareQuizCreate =
          /^(make|maka|mak|mae|create|creat|build|start)\s+(a\s+|an\s+)?quiz\b/i.test(
            displayText.trim()
          ) && !/\b(histology|embryology|anatomy)\b/i.test(displayText);
        // Free-topic asks (“make a quiz on connective tissue”) should stay on
        // the local size-suggestion chip flow — not MCP title prompts.
        // Only the topic → title-picker step stays local; a full quiz draft does not.
        const freeTopicSuggestionFlow = Boolean(local?.titlePicker && local?.draft?.topic_rules?.[0]?.catalogue);
        // Local replies only for the quiz-setup starters and feature guides they were written for.
        const useLocalOnly =
          !!local &&
          !confirmCreate &&
          !resolvedSourceId &&
          (turn.lane === LANES.QUIZ || turn.lane === LANES.GUIDE) &&
          !isPresentationCreateRequest(displayText) &&
          (guideIntent ||
            bareQuizCreate ||
            freeTopicSuggestionFlow ||
            /^create a quiz\.?$/i.test(displayText.trim()) ||
            /^cat:/i.test(displayText.trim()) ||
            // Full quiz asks (“20 questions, 20 min…”) go to the server, which splits
            // counts over rules and asks for the title; local drafts are offline-only.
            /private question bank/i.test(displayText) ||
            /help me with my quizzes|ask about my quizzes/i.test(displayText));

        if (useLocalOnly) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: local.reply,
              status: MESSAGE_STATUS.SENT,
            }),
          });
          if (local.draft && quizDraftApi?.setDraft) {
            quizDraftApi.setDraft({
              ...local.draft,
              last_created_quiz: seededDraft?.last_created_quiz || null,
              last_lane: turn.lane,
            });
          } else if (bareQuizCreate && quizDraftApi?.setDraft) {
            // “Create a quiz” starts a new draft; an old unconfirmed one must not leak in.
            quizDraftApi.setDraft({
              ...emptyQuizDraft(),
              last_created_quiz: seededDraft?.last_created_quiz || null,
              last_lane: turn.lane,
            });
          }
          setCopilotUi((prev) => ({
            ...prev,
            optionChips: (local.options || []).map((o) => ({
              id: o.id,
              label: o.label,
              prompt: o.prompt || o.label,
            })),
            confirmation: local.confirmation || null,
            confirming: false,
            titlePicker: local.titlePicker || prev.titlePicker || null,
          }));
          return;
        }

        if (
          copilotLane ||
          shouldUseQuizCopilot(displayText) ||
          answeringDeck ||
          confirmCreate ||
          resultsIntent ||
          usersIntent
        ) {
          if (confirmCreate && !confirmRequestIdRef.current) {
            confirmRequestIdRef.current = `quiz-create-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`;
          }
          setCopilotUi((prev) => ({ ...prev, confirming: !!confirmCreate }));

          const intentHint =
            resolvedSourceId && fileIntent === "quiz"
              ? "\nMake a quiz from the uploaded file"
              : resolvedSourceId && fileIntent === "presentation"
                ? "\nMake a presentation from the uploaded file"
                : "";
          const res = await quizCopilotChatCall({
            message: `${displayText}${intentHint}`,
            draft: quizDraftApi?.draft || null,
            confirm_create: !!confirmCreate,
            client_request_id: confirmCreate ? confirmRequestIdRef.current : null,
            dashboard: dashboardContext(),
            history: buildHistory(stateRef.current.messages),
            source_id: resolvedSourceId || (turn.usesFile ? recentSourceRef.current?.id : null) || null,
            conversation_id: stateRef.current.conversationId,
          });

          // Backend says the conversation is full: drop the prompt, keep the text.
          if (res?.code === "CONVERSATION_LIMIT") {
            dispatch({ type: "REMOVE_MESSAGE", id: userMsg.id });
            dispatch({ type: "SET_DRAFT", draft: inputBeforeSend || displayText });
            dispatch({ type: "SET_SERVER_LIMIT", reached: true });
            return;
          }

          if (res?.status === 401 || res?.status === 403) {
            dispatch({
              type: "UPDATE_MESSAGE",
              id: userMsg.id,
              patch: { status: MESSAGE_STATUS.FAILED },
            });
            dispatch({
              type: "SET_ERROR",
              error:
                res?.message ||
                "Your session is no longer valid or you are not authorized for Quiz Copilot.",
            });
            return;
          }

          if (!res || (typeof res.status === "number" && res.status >= 400)) {
            // Server returned an error payload with a usable reply — show it and stop.
            if (res?.reply) {
              dispatch({
                type: "UPDATE_MESSAGE",
                id: userMsg.id,
                patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
              });
              dispatch({
                type: "APPEND_MESSAGE",
                message: createMessage({
                  role: "assistant",
                  content: res.reply,
                  status: MESSAGE_STATUS.SENT,
                }),
              });
              setCopilotUi((prev) => ({
                ...prev,
                confirming: false,
                optionChips: Array.isArray(res?.ui_actions?.options)
                  ? res.ui_actions.options.map((o) => ({
                      id: o.id,
                      label: o.label,
                      prompt: o.prompt || o.label,
                    }))
                  : [],
              }));
              return;
            }

            // Confirm while offline → open Create Quiz with the local draft filled in.
            if (confirmCreate && quizDraftApi?.draft) {
              quizDraftApi.setDraft({
                ...quizDraftApi.draft,
                awaiting_confirmation: false,
              });
              quizDraftApi.applyCopilotResult?.({
                draft: quizDraftApi.draft,
              });
              quizDraftApi.requestOpenQuizForm?.();
              applyAssistantText(
                userMsg.id,
                "Quiz Copilot is offline, so I opened the Create Quiz form with your draft. Review and save it there."
              );
              navigate("/quiz/create");
              return;
            }

            // Results offline → hit the results API directly and analyze.
            if (resultsIntent) {
              const analysis = await loadResultsAnalysisLocally();
              applyAssistantText(userMsg.id, analysis.reply, analysis.options);
              return;
            }

            // Users/directory offline → university students (admin) or class-scoped results.
            if (usersIntent) {
              const directory = await loadUsersDirectoryLocally();
              applyAssistantText(userMsg.id, directory.reply, directory.options);
              return;
            }

            // Presentation outline: prefer a local draft card so edit/delete/confirm still work offline.
            if (presentationIntent) {
              const bareAsk = /^(make|create|creat|build)\s+(a\s+)?[a-z]*pres[a-z]*t[a-z]*ions?\.?$/i.test(
                displayText.trim()
              );
              if (bareAsk) {
                dispatch({
                  type: "UPDATE_MESSAGE",
                  id: userMsg.id,
                  patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
                });
                dispatch({
                  type: "APPEND_MESSAGE",
                  message: createMessage({
                    role: "assistant",
                    content:
                      "What topic should the presentation cover? I’ll ask how many slides next.",
                    status: MESSAGE_STATUS.SENT,
                  }),
                });
                presentationSetupRef.current = { step: "topic" };
                setCopilotUi((prev) => ({
                  ...prev,
                  confirmation: null,
                  pendingPresentation: null,
                  optionChips: [],
                  confirming: false,
                }));
                return;
              }

              // Offline: say so. A placeholder deck would look like real slides.
              applyAssistantText(
                userMsg.id,
                "I couldn’t reach the presentation service just now, so I haven’t drafted any slides. Please try again in a moment.",
                [{ id: "retry_presentation", label: "Try again", prompt: displayText }]
              );
              return;
            }

            const fallback =
              getLocalAssistantFallback(displayText, {
                draft: quizDraftApi?.draft || null,
              }) || {
                reply:
                  "I couldn’t reach the Quiz Copilot service just now. You can still pick a topic below, or try again in a moment.",
                options: [
                  { id: "cat:histology", label: "Histology (Microlab)", prompt: "cat:histology" },
                  { id: "cat:embryology", label: "Embryology (Microlab)", prompt: "cat:embryology" },
                  { id: "cat:anatomy", label: "Anatomy (Microlab)", prompt: "cat:anatomy" },
                  {
                    id: "bank:private",
                    label: "My private question bank",
                    prompt: "Create a quiz using my private question bank.",
                  },
                ],
              };
            if (fallback.draft && quizDraftApi?.setDraft) {
              quizDraftApi.setDraft(fallback.draft);
            }
            dispatch({
              type: "UPDATE_MESSAGE",
              id: userMsg.id,
              patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
            });
            dispatch({
              type: "APPEND_MESSAGE",
              message: createMessage({
                role: "assistant",
                content: fallback.reply,
                status: MESSAGE_STATUS.SENT,
              }),
            });
            setCopilotUi((prev) => ({
              ...prev,
              optionChips: (fallback.options || []).map((o) => ({
                id: o.id,
                label: o.label,
                prompt: o.prompt || o.label,
              })),
              confirmation: fallback.confirmation || null,
              pendingPresentation: null,
              confirming: false,
            }));
            return;
          }

          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });

          if (res.reply) {
            dispatch({
              type: "APPEND_MESSAGE",
              message: createMessage({
                role: "assistant",
                content: res.reply,
                status: MESSAGE_STATUS.SENT,
              }),
            });
          }

          // Only real server drafts are shown. A template deck built from the user's
          // words (“Types / structure of <message>”) is not content and is never invented here.
          applyCopilotResponse(res);
          if (resolvedSourceId && turn.usesFile && !res?.ui_actions?.ask_source_deck) {
            // Used: detach it so later messages aren't all about the file, but keep it
            // for follow-ups that refer to it.
            recentSourceRef.current = { id: resolvedSourceId, name: activeSource?.name || null };
            sourceRef.current = null;
            setSourceAttachment(null);
          }

          // Prefer Results chips for analysis turns.
          if (resultsIntent) {
            const opts = Array.isArray(res?.ui_actions?.options) ? res.ui_actions.options : [];
            setCopilotUi((prev) => ({
              ...prev,
              optionChips: (opts.length ? opts : RESULTS_OPTION_CHIPS).map((o) => ({
                id: o.id,
                label: o.label,
                prompt: o.prompt || o.label,
              })),
            }));
          }

          if (res.quiz?.id) {
            confirmRequestIdRef.current = null;
          }
          return;
        }

        // Generic local fallback for starter asks that didn't route to quiz-copilot
        if (local) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
          });
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: local.reply,
              status: MESSAGE_STATUS.SENT,
            }),
          });
          if (local.draft && quizDraftApi?.setDraft) {
            quizDraftApi.setDraft(local.draft);
          }
          setCopilotUi((prev) => ({
            ...prev,
            optionChips: (local.options || []).map((o) => ({
              id: o.id,
              label: o.label,
              prompt: o.prompt || o.label,
            })),
            confirmation: local.confirmation || null,
            confirming: false,
            titlePicker: local.titlePicker || prev.titlePicker || null,
          }));
          return;
        }

        // Generic tutor chat (Phase 1 path)
        const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0] || "en";
        const threadId = stateRef.current.threadId;
        const formData = new FormData();
        formData.append("body", text);
        formData.append("type", "ai");
        formData.append("entity_type", "aiQuestion");
        formData.append("language", locale);
        formData.append("dashboard_section", routeContext.key);
        formData.append("dashboard_section_label", routeContext.label);
        if (threadId) {
          formData.append("thread_id", threadId);
          formData.append("title", "histology");
        }

        const res = threadId ? await askAIChatCall(formData) : await askTutorChatCall(formData);

        if (res?.status === 401 || res?.status === 403) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error: "Your session is no longer valid. Please sign in again.",
          });
          return;
        }

        if (res?.status === 402 || res?.code === "INSUFFICIENT_CREDITS") {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error:
              res?.message ||
              "Insufficient AI credits to start a new conversation. Existing chats remain available.",
          });
          return;
        }

        const failed =
          !res ||
          (typeof res.status === "number" && res.status >= 400) ||
          (!threadId && !res.thread_id && !res?.messages && !res?.tutor);

        if (failed) {
          dispatch({
            type: "UPDATE_MESSAGE",
            id: userMsg.id,
            patch: { status: MESSAGE_STATUS.FAILED },
          });
          dispatch({
            type: "SET_ERROR",
            error: res?.message || "The message could not be sent. Check your connection and retry.",
          });
          return;
        }

        dispatch({
          type: "UPDATE_MESSAGE",
          id: userMsg.id,
          patch: { status: MESSAGE_STATUS.SENT, retryPayload: null },
        });

        const nextThreadId = res.thread_id || threadId;
        if (nextThreadId && nextThreadId !== threadId) {
          dispatch({ type: "SET_THREAD", threadId: nextThreadId });
        }

        const aiText = sanitizeTutorContent(
          res?.tutor?.answer ||
            res?.messages?.[1]?.content ||
            res?.messages?.[res?.messages?.length - 1]?.content ||
            ""
        );

        if (aiText) {
          dispatch({
            type: "APPEND_MESSAGE",
            message: createMessage({
              role: "assistant",
              content: aiText,
              status: MESSAGE_STATUS.SENT,
            }),
          });
        }
      } catch (err) {
        dispatch({
          type: "UPDATE_MESSAGE",
          id: userMsg.id,
          patch: { status: MESSAGE_STATUS.FAILED },
        });
        dispatch({
          type: "SET_ERROR",
          error:
            err?.message?.includes("Network") || !navigator.onLine
              ? "You appear to be offline. Reconnect and retry your message."
              : "Network error while contacting the AI assistant. Please retry.",
        });
      } finally {
        dispatch({ type: "SET_SENDING", sending: false });
        setCopilotUi((prev) => ({ ...prev, confirming: false }));
      }
    },
    [
      auth,
      isAuthed,
      routeContext,
      dashboardContext,
      copilotUi.pendingPresentation,
      copilotUi.confirmation,
      shouldUseQuizCopilot,
      quizDraftApi,
      applyCopilotResponse,
      applyAssistantText,
      loadResultsAnalysisLocally,
      navigate,
    ]
  );

  /* ---------- Draft cards: undo + direct edits (never counted as messages) ---------- */

  const pushUndo = useCallback((label) => {
    setUndoStack((prev) => [
      ...prev.slice(-(UNDO_DEPTH - 1)),
      { label, quiz: quizDraftRef.current, document: documentDraftRef.current },
    ]);
  }, []);

  const undoDraftEdit = useCallback(() => {
    setUndoStack((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      quizDraftApi?.setDraft?.(last.quiz || {});
      setDocumentDraft(last.document || null);
      setCopilotUi((ui) => ({
        ...ui,
        confirmation: last.quiz?.awaiting_confirmation ? previewFromDraft(last.quiz) : ui.confirmation,
      }));
      return prev.slice(0, -1);
    });
  }, [quizDraftApi]);

  const editPresentation = useCallback(
    (fn, label) => {
      const current = quizDraftRef.current?.pending_presentation;
      if (!current?.slides) return false;
      const next = fn(current);
      if (!next || next === current) return false;
      pushUndo(label);
      quizDraftApi?.setDraft?.((d) => ({ ...d, pending_presentation: next }));
      return true;
    },
    [pushUndo, quizDraftApi]
  );

  const editQuiz = useCallback(
    (fn, label, { quizConfig = false } = {}) => {
      const current = quizDraftRef.current;
      if (!current) return false;
      const next = fn(current);
      if (!next || next === current) return false;
      pushUndo(label);
      quizDraftApi?.setDraft?.(next);
      // Edits clear the earlier approval; keep the card open while editing.
      if (quizConfig) {
        setCopilotUi((prev) => (prev.quizEditorOpen ? prev : { ...prev, quizEditorOpen: true }));
      }
      return true;
    },
    [pushUndo, quizDraftApi]
  );

  const editDocument = useCallback(
    (fn, label) => {
      const current = documentDraftRef.current;
      if (!current) return false;
      const next = fn(current);
      if (!next || next === current) return false;
      pushUndo(label);
      setDocumentDraft(next);
      return true;
    },
    [pushUndo]
  );

  const appendEvent = useCallback((content) => {
    dispatch({
      type: "APPEND_MESSAGE",
      message: createMessage({ role: "assistant", content, status: MESSAGE_STATUS.SENT, event: true }),
    });
  }, []);

  /**
   * Approve / save / discard actions from a draft card. They call the copilot
   * without adding a prompt to the chat, and the result is shown as a non-counted
   * event, so a full conversation can still finish its drafts.
   */
  const runCardAction = useCallback(
    async (command, { confirmCreate = false, draft = null } = {}) => {
      if (stateRef.current.sending) return null;
      if (!isAuthed) {
        dispatch({ type: "SET_ERROR", error: "Please sign in again to use the AI assistant." });
        return null;
      }
      dispatch({ type: "SET_ERROR", error: null });
      dispatch({ type: "SET_SENDING", sending: true });
      setCopilotUi((prev) => ({ ...prev, confirming: true }));
      try {
        const conversationId = stateRef.current.conversationId || (await ensureConversation());
        if (confirmCreate && !confirmRequestIdRef.current) {
          confirmRequestIdRef.current = newRequestId("quiz-create");
        }
        const res = await quizCopilotChatCall({
          message: command,
          draft: draft || quizDraftRef.current,
          confirm_create: confirmCreate,
          client_request_id: confirmCreate ? confirmRequestIdRef.current : null,
          dashboard: dashboardContext(),
          history: [],
          conversation_id: conversationId,
          card_action: true,
        });
        if (!res || (typeof res.status === "number" && res.status >= 400)) {
          dispatch({
            type: "SET_ERROR",
            error: res?.message || res?.reply || "That didn't go through. Check your connection and try again.",
          });
          return res;
        }
        if (res.reply) appendEvent(res.reply);
        applyCopilotResponse(res);
        if (res.quiz?.id || res.presentation?.id || res.already_created) {
          confirmRequestIdRef.current = null;
          setCopilotUi((prev) => ({ ...prev, quizEditorOpen: res.quiz?.id ? false : prev.quizEditorOpen }));
          // An approved draft can't be brought back with undo.
          setUndoStack([]);
        }
        return res;
      } finally {
        dispatch({ type: "SET_SENDING", sending: false });
        setCopilotUi((prev) => ({ ...prev, confirming: false }));
      }
    },
    [isAuthed, ensureConversation, dashboardContext, appendEvent, applyCopilotResponse]
  );

  /* Presentation draft */

  const confirmPresentationDraft = useCallback(() => {
    setCopilotUi((prev) => ({ ...prev, confirmation: null }));
    return runCardAction("presentation_confirm");
  }, [runCardAction]);

  // Discarding is local (the backend keeps no copy of the draft), so Undo can restore it.
  const discardPresentationDraft = useCallback(() => {
    if (!quizDraftRef.current?.pending_presentation) return;
    pushUndo("Discarded presentation draft");
    quizDraftApi?.setDraft?.((d) => ({ ...d, pending_presentation: null }));
    appendEvent("Discarded the presentation draft. Nothing was saved — use Undo to bring it back.");
  }, [pushUndo, quizDraftApi, appendEvent]);

  const updatePresentationSlide = useCallback(
    (index, patch) => editPresentation((p) => deckOps.updateSlide(p, index, patch), `Edited slide ${index + 1}`),
    [editPresentation]
  );
  const removePresentationSlide = useCallback(
    (index) => editPresentation((p) => deckOps.removeSlide(p, index), `Removed slide ${index + 1}`),
    [editPresentation]
  );
  const removePresentationSlide3d = useCallback(
    (index) => editPresentation((p) => deckOps.clearSlide3d(p, index), `Removed 3D from slide ${index + 1}`),
    [editPresentation]
  );
  const duplicatePresentationSlide = useCallback(
    (index) => editPresentation((p) => deckOps.duplicateSlide(p, index), `Duplicated slide ${index + 1}`),
    [editPresentation]
  );
  const movePresentationSlide = useCallback(
    (from, to) => editPresentation((p) => deckOps.moveSlide(p, from, to), `Moved slide ${from + 1}`),
    [editPresentation]
  );
  /** Returns the new slide's id so the card can open its editor. */
  const addPresentationSlide = useCallback(
    (afterIndex = null) => {
      let newId = null;
      editPresentation((p) => {
        const next = deckOps.addSlide(p, afterIndex);
        const known = new Set((p.slides || []).map((sl) => sl.id));
        newId = next.slides.find((sl) => !known.has(sl.id))?.id || null;
        return next;
      }, "Added a slide");
      return newId;
    },
    [editPresentation]
  );
  const setPresentationSlideVisual = useCallback(
    (index, item) =>
      editPresentation((p) => deckOps.setSlideVisual(p, index, item), `Changed visual on slide ${index + 1}`),
    [editPresentation]
  );
  const clearPresentationSlideImage = useCallback(
    (index) => editPresentation((p) => deckOps.clearSlideImage(p, index), `Removed image from slide ${index + 1}`),
    [editPresentation]
  );
  const setPresentationSlideTextOnly = useCallback(
    (index) => editPresentation((p) => deckOps.setTextOnly(p, index), `Made slide ${index + 1} text only`),
    [editPresentation]
  );
  /** "Replace content" asks the AI to rewrite one slide; it is a real chat turn. */
  const rewritePresentationSlide = useCallback(
    (index, instruction) => {
      const how = String(instruction || "").trim() || "make it clearer for students";
      return sendMessage(`Rewrite slide ${index + 1}: ${how}`);
    },
    [sendMessage]
  );

  const searchPresentationLibrary = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (!q) return [];
      const res = await quizCopilotChatCall({
        message: `presentation_library_search:${q}`,
        draft: quizDraftRef.current || null,
        dashboard: dashboardContext(),
        history: [],
        conversation_id: stateRef.current.conversationId,
        card_action: true,
      });
      const results = res?.library_results || res?.ui_actions?.library_results || [];
      return Array.isArray(results) ? results : [];
    },
    [dashboardContext]
  );

  const addPresentationLibrarySlide = useCallback(
    (item, afterIndex = null) =>
      editPresentation((p) => deckOps.addMediaSlide(p, item, afterIndex), `Added “${item?.label || "library"}” slide`),
    [editPresentation]
  );

  /* Quiz draft */

  const openQuizEditor = useCallback(() => {
    setCopilotUi((prev) => ({
      ...prev,
      quizEditorOpen: true,
      confirmation: prev.confirmation || previewFromDraft(quizDraftRef.current),
    }));
  }, []);

  /** Kept for the full Create Quiz form; the chat card is the default editor. */
  const openQuizFullForm = useCallback(() => {
    quizDraftApi?.requestOpenQuizForm?.();
    if (!String(routeContext?.pathname || "").startsWith("/quiz/create")) navigate("/quiz/create");
  }, [quizDraftApi, navigate, routeContext?.pathname]);

  const editQuizConfiguration = openQuizEditor;

  const updateQuizSettings = useCallback(
    (patch) => editQuiz((d) => quizOps.updateQuizSettings(d, patch), "Edited quiz settings", { quizConfig: true }),
    [editQuiz]
  );
  const updateQuizRule = useCallback(
    (index, patch) => editQuiz((d) => quizOps.updateTopicRule(d, index, patch), "Edited question rule", { quizConfig: true }),
    [editQuiz]
  );
  const removeQuizRule = useCallback(
    (index) => editQuiz((d) => quizOps.removeTopicRule(d, index), "Removed question rule", { quizConfig: true }),
    [editQuiz]
  );

  /** Persist the bank pick reviewed in the draft card (no undo / no chat event). */
  const setQuizLockedRefs = useCallback(
    (refs) => {
      quizDraftApi?.setDraft?.((d) => {
        const next = quizOps.setLockedQuestionRefs(d, refs);
        if (
          JSON.stringify(d?.locked_question_refs || []) ===
          JSON.stringify(next.locked_question_refs || [])
        ) {
          return d;
        }
        return next;
      });
    },
    [quizDraftApi]
  );

  /** Re-approves exactly what is shown (edits clear the earlier preview), then creates. */
  const confirmCreateQuiz = useCallback(async () => {
    let draft = quizDraftRef.current;
    if (!draft) return null;
    if (!Array.isArray(draft.locked_question_refs) || !draft.locked_question_refs.length) {
      dispatch({
        type: "SET_ERROR",
        error: "Review the picked questions below (use Randomize if needed) before creating the quiz.",
      });
      return null;
    }
    if (!draft.awaiting_confirmation || !draft.confirmation_fingerprint) {
      // Uses `confirming`, not `sending`: runCardAction below bails while sending.
      setCopilotUi((prev) => ({ ...prev, confirming: true }));
      const preview = await invokeMcpToolCall({ tool: "preview_quiz_configuration", draft });
      setCopilotUi((prev) => ({ ...prev, confirming: false }));
      const errors = preview?.result?.validation?.errors || [];
      if (!preview?.ok || !preview?.result?.needs_confirmation || !preview?.draft) {
        dispatch({
          type: "SET_ERROR",
          error: errors.length
            ? `Fix the quiz first: ${errors.join(" ")}`
            : preview?.message || preview?.error || "Couldn't check the quiz configuration.",
        });
        return null;
      }
      // Keep the professor's reviewed bank pick across the MCP preview refresh.
      draft = {
        ...preview.draft,
        locked_question_refs: draft.locked_question_refs,
      };
      quizDraftApi?.setDraft?.(draft);
    }
    return runCardAction("Create Quiz", { confirmCreate: true, draft });
  }, [quizDraftApi, runCardAction]);

  const updateGeneratedQuestion = useCallback(
    (index, patch) =>
      editQuiz((d) => quizOps.updateGeneratedQuestion(d, index, patch), `Edited question ${index + 1}`),
    [editQuiz]
  );
  const removeGeneratedQuestion = useCallback(
    (index) => editQuiz((d) => quizOps.removeGeneratedQuestion(d, index), `Removed question ${index + 1}`),
    [editQuiz]
  );
  const saveGeneratedQuestions = useCallback(() => runCardAction("confirm_save_generated"), [runCardAction]);
  const discardGeneratedQuestions = useCallback(() => {
    if (editQuiz((d) => ({ ...d, pending_generated_questions: null }), "Discarded draft questions")) {
      appendEvent("Discarded the draft questions. Nothing was saved — use Undo to bring them back.");
    }
  }, [editQuiz, appendEvent]);

  /* Quiz title picker (Microlab bank titles or the user's own private titles) */

  const openTitlePicker = useCallback((picker) => {
    setCopilotUi((prev) => ({ ...prev, titlePicker: { source: "microlab", ...picker } }));
  }, []);

  const closeTitlePicker = useCallback(() => {
    setCopilotUi((prev) => ({ ...prev, titlePicker: null }));
  }, []);

  const loadQuizTitleOptions = useCallback(async ({ source, catalogue }) => {
    const res = await invokeMcpToolCall({
      tool: "list_quiz_title_options",
      args: { source, catalogue },
    });
    if (!res?.ok || res?.result?.error) {
      throw new Error(res?.result?.error || res?.error || res?.message || "Couldn't load titles.");
    }
    return res.result;
  }, []);

  /** Puts the chosen titles into the quiz draft and opens it for review (undoable). */
  const applyQuizTitles = useCallback(
    (selection) => {
      const base = quizDraftRef.current || {};
      pushUndo("Chose quiz titles");
      quizDraftApi?.setDraft?.(applyTitleSelection(base, selection));
      setCopilotUi((prev) => ({ ...prev, titlePicker: null, quizEditorOpen: true, optionChips: [] }));
      const what =
        selection.source === "private"
          ? `${selection.titles.length} of your title${selection.titles.length === 1 ? "" : "s"}`
          : selection.wholeCatalogue
            ? `all ${catalogueName(selection.catalogue)} titles`
            : `${selection.titles.length} ${catalogueName(selection.catalogue)} title${selection.titles.length === 1 ? "" : "s"}`;
      appendEvent(
        `Using ${what} · ${selection.numQuestions} questions · ${selection.timeLimit} min. Review the quiz below, then tap Create quiz.`
      );
    },
    [pushUndo, quizDraftApi, appendEvent]
  );

  /* Document workspace */

  const updateDocumentSection = useCallback(
    (id, patch) => editDocument((d) => docOps.updateSection(d, id, patch), "Edited section"),
    [editDocument]
  );
  const removeDocumentSection = useCallback(
    (id) => editDocument((d) => docOps.removeSection(d, id), "Removed section"),
    [editDocument]
  );
  const addDocumentSection = useCallback(
    (afterId = null) => editDocument((d) => docOps.addSection(d, afterId), "Added section"),
    [editDocument]
  );
  const moveDocumentSection = useCallback(
    (id, delta) => editDocument((d) => docOps.moveSection(d, id, delta), "Moved section"),
    [editDocument]
  );

  const rewriteDocumentSection = useCallback(
    async (id, instruction) => {
      const section = documentDraftRef.current?.sections?.find((s) => s.id === id);
      if (!section?.body?.trim()) return "This section is empty.";
      const res = await rewriteSectionCall({ text: section.body, instruction });
      if (!res?.text) return res?.message || "Couldn't rewrite that section.";
      editDocument((d) => docOps.updateSection(d, id, { body: res.text }), "AI rewrite");
      return null;
    },
    [editDocument]
  );

  /**
   * Save = update the working text the assistant reads (temporary draft).
   * The uploaded original is never overwritten.
   */
  const saveDocumentDraft = useCallback(async () => {
    const doc = documentDraftRef.current;
    if (!doc) return null;
    const res = await updateAssistantSourceTextCall(doc.source_id, {
      text: docOps.sectionsToText(doc.sections),
      file_name: doc.file_name,
    });
    const source = res?.source;
    if (!source?.id) return res?.message || "Couldn't save the document draft.";
    setDocumentDraft((prev) =>
      prev
        ? {
            ...prev,
            source_id: source.id,
            dirty: false,
            applied_at: new Date().toISOString(),
            saved_sections: prev.sections,
          }
        : prev
    );
    const ready = {
      name: doc.file_name,
      size: 0,
      status: "ready",
      error: "",
      imageCount: Number(doc.image_count) || 0,
      id: source.id,
    };
    sourceRef.current = ready;
    setSourceAttachment(ready);
    return null;
  }, []);

  const discardDocumentChanges = useCallback(
    () =>
      editDocument(
        (d) => ({ ...d, sections: d.saved_sections || d.sections, dirty: false }),
        "Discarded document changes"
      ),
    [editDocument]
  );

  const closeDocumentDraft = useCallback(() => {
    if (!documentDraftRef.current) return;
    pushUndo("Closed document");
    setDocumentDraft(null);
  }, [pushUndo]);

  const applyDocumentTo = useCallback(
    async (kind) => {
      const problem = await saveDocumentDraft();
      if (problem) {
        dispatch({ type: "SET_ERROR", error: problem });
        return;
      }
      sendMessage(
        kind === "quiz" ? "Make a quiz from the uploaded file" : "Make a presentation from the uploaded file",
        { sourceId: sourceRef.current?.id || null }
      );
    },
    [saveDocumentDraft, sendMessage]
  );

  const openCreatedQuiz = useCallback(() => {
    const quiz = copilotUi.createdQuiz;
    if (!quiz?.id) return;
    navigate(`/quiz/create?highlight=${encodeURIComponent(quiz.id)}`);
  }, [copilotUi.createdQuiz, navigate]);

  const openCreatedQuizResults = useCallback(() => {
    navigate("/quiz/results");
  }, [navigate]);

  const retryMessage = useCallback(
    (message) => {
      if (!message?.retryPayload?.text && message?.role !== "user") return;
      const text = message.retryPayload?.text || message.content;
      sendMessage(text, {
        retryOfId: message.id,
        sourceId: message.retryPayload?.sourceId || null,
      });
    },
    [sendMessage]
  );

  const messageCount = countMessages(state.messages);
  const limit = getLimitState(messageCount, { serverLimitReached: state.serverLimitReached });
  const conversationLimitReached = limit.limitReached;

  const value = useMemo(
    () => ({
      ...state,
      routeContext,
      conversationLimitReached,
      limit,
      messageCount,
      open,
      close,
      toggle,
      setDraft,
      clearError,
      sourceAttachment,
      attachSourceFile,
      clearSourceAttachment,
      sendMessage,
      retryMessage,
      startNewConversation,
      copilotUi,
      confirmCreateQuiz,
      editQuizConfiguration,
      confirmPresentationDraft,
      discardPresentationDraft,
      updatePresentationSlide,
      removePresentationSlide,
      removePresentationSlide3d,
      duplicatePresentationSlide,
      movePresentationSlide,
      addPresentationSlide,
      setPresentationSlideVisual,
      clearPresentationSlideImage,
      setPresentationSlideTextOnly,
      rewritePresentationSlide,
      searchPresentationLibrary,
      addPresentationLibrarySlide,
      openQuizEditor,
      openQuizFullForm,
      updateQuizSettings,
      updateQuizRule,
      removeQuizRule,
      setQuizLockedRefs,
      updateGeneratedQuestion,
      removeGeneratedQuestion,
      saveGeneratedQuestions,
      discardGeneratedQuestions,
      openTitlePicker,
      closeTitlePicker,
      loadQuizTitleOptions,
      applyQuizTitles,
      documentDraft,
      updateDocumentSection,
      removeDocumentSection,
      addDocumentSection,
      moveDocumentSection,
      rewriteDocumentSection,
      saveDocumentDraft,
      discardDocumentChanges,
      closeDocumentDraft,
      applyDocumentTo,
      undoStack,
      undoDraftEdit,
      history,
      openHistory,
      closeHistory,
      refreshHistory,
      openConversation: loadConversation,
      renameConversation,
      deleteConversation,
      openCreatedQuiz,
      openCreatedQuizResults,
      clearLocalPersistence: () => {
        if (state.ownerUserId) clearPersistedConversation(state.ownerUserId);
      },
    }),
    [
      state,
      routeContext,
      conversationLimitReached,
      open,
      close,
      toggle,
      setDraft,
      clearError,
      sourceAttachment,
      attachSourceFile,
      clearSourceAttachment,
      sendMessage,
      retryMessage,
      startNewConversation,
      copilotUi,
      confirmCreateQuiz,
      editQuizConfiguration,
      confirmPresentationDraft,
      discardPresentationDraft,
      updatePresentationSlide,
      removePresentationSlide,
      removePresentationSlide3d,
      duplicatePresentationSlide,
      movePresentationSlide,
      addPresentationSlide,
      setPresentationSlideVisual,
      clearPresentationSlideImage,
      setPresentationSlideTextOnly,
      rewritePresentationSlide,
      searchPresentationLibrary,
      addPresentationLibrarySlide,
      openQuizEditor,
      openQuizFullForm,
      updateQuizSettings,
      updateQuizRule,
      removeQuizRule,
      setQuizLockedRefs,
      updateGeneratedQuestion,
      removeGeneratedQuestion,
      saveGeneratedQuestions,
      discardGeneratedQuestions,
      openTitlePicker,
      closeTitlePicker,
      loadQuizTitleOptions,
      applyQuizTitles,
      documentDraft,
      updateDocumentSection,
      removeDocumentSection,
      addDocumentSection,
      moveDocumentSection,
      rewriteDocumentSection,
      saveDocumentDraft,
      discardDocumentChanges,
      closeDocumentDraft,
      applyDocumentTo,
      undoStack,
      undoDraftEdit,
      history,
      openHistory,
      closeHistory,
      refreshHistory,
      loadConversation,
      renameConversation,
      deleteConversation,
      limit,
      messageCount,
      openCreatedQuiz,
      openCreatedQuizResults,
    ]
  );

  return (
    <AIAssistantContext.Provider value={value}>{children}</AIAssistantContext.Provider>
  );
}

function previewFromDraft(draft) {
  if (!draft) return null;
  const total = (draft.topic_rules || []).reduce(
    (sum, r) => sum + (Number(r.num_questions) || 0),
    0
  );
  return {
    title: draft.title || "(untitled)",
    time_limit_minutes: draft.time_limit_minutes || 30,
    total_questions: total,
    topic_rules: (draft.topic_rules || []).map((r) => ({
      source: r.bank_source,
      catalogue: r.catalogue || null,
      topic_label: r.topic_label || null,
      subtitles:
        Array.isArray(r.subtitle_labels) && r.subtitle_labels.length
          ? r.subtitle_labels
          : Array.isArray(r.quiz_title_ids) && r.quiz_title_ids.length
            ? [`${r.quiz_title_ids.length} selected`]
            : ["All"],
      difficulty: r.difficulty_filter || "any",
      num_questions: r.num_questions,
    })),
    instructions_preview: String(draft.rules_html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 240),
  };
}

AIAssistantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAIAssistant() {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) {
    throw new Error("useAIAssistant must be used within AIAssistantProvider");
  }
  return ctx;
}

export function useAIAssistantOptional() {
  return useContext(AIAssistantContext);
}

export default AIAssistantProvider;
